import React, { FC, memo } from 'react'

import { useFragment } from '@apollo/client'
import {
  CommunityPost as BaseCommunityPost,
  CommunityPostProps,
} from '@factorialco/f0-react/dist/experimental'
import DOMPurify from 'dompurify'
import { emojiIndex } from 'emoji-mart'
import {
  CommunityPostDataFragment,
  CommunityPostDataFragmentDoc,
} from 'generated/resources/graphql'
import compact from 'lodash/compact'
import { useIntersectionObserver } from 'usehooks-ts'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import buildUrl from 'modules/core/lib/buildUrl'
import { stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { useReactions } from 'modules/engagement/lib/reactions'

type Callbacks = {
  onGroupClick: (groupId: string) => void
  onPostClick: (postId: string) => void
  onCommentClick: (postId: string) => void
  onReactionInteraction?: (postId: string, reactionId: string) => void
}

type Props = Callbacks & {
  id: number
  onVisible?: () => void
}

const useNormalizedCommunityPostData = (
  data: CommunityPostDataFragment,
  callbacks?: Callbacks
): CommunityPostProps | undefined => {
  const { navigation } = useNavigation()

  const {
    access: { id: currentAccessId },
  } = useCurrentSession()

  const { onEmojiSelected } = useReactions({
    postId: data.id,
  })

  const author = data.author
  const authorEmployeeId = author?.employee?.id

  const preferredName = author?.employee?.preferredName

  const firstName = preferredName ? preferredName : author?.firstName
  const lastName = author?.lastName

  const event =
    data.eventOn && data.startsAt && data.title && data.type === 'Posts::V2::Event'
      ? {
          date: stringToDate(data.startsAt, 'backend_datetime').toDate(),
          title: data.title,
          place: data.location ?? undefined,
          imageUrl: data.coverImageUrl ?? undefined,
        }
      : undefined

  const getEmojiData = (code: string) => {
    let emojiData = emojiIndex.emojis?.[code]

    if (
      emojiData &&
      typeof emojiData === 'object' &&
      !('native' in emojiData) &&
      Object.keys(emojiData).every((k) => !isNaN(Number(k)))
    ) {
      const variants = Object.values(emojiData)
      emojiData = variants.find((e) => !e.skin) || variants[0]
    } else if (!emojiData) {
      const searchResults = emojiIndex.search(code)
      emojiData =
        searchResults?.find((e) => e.id === code || ('native' in e && e.native === code)) ||
        searchResults?.[0]
    }

    return emojiData
  }

  return {
    id: `${data.id}`,
    author:
      firstName && lastName && author
        ? {
            firstName,
            lastName,
            avatarUrl: buildUrl(author.avatar) ?? undefined,
            url: authorEmployeeId
              ? navigation.employees.id.fullPath({
                  id: authorEmployeeId,
                })
              : undefined,
          }
        : undefined,
    group: {
      title: data.group?.title ?? '',
      onClick: () => callbacks?.onGroupClick(`${data.group?.id}`),
    },
    createdAt: stringToDate(data.createdAt, 'backend_datetime').toDate(),
    title: data.title ?? '',
    description: DOMPurify.sanitize(data.description ?? '') ?? undefined,
    mediaUrl: data.coverImageUrl ?? undefined,
    event,
    counters: {
      views: `${data.visitsCount} ${i18n.tp('posts.visits.count', {
        count: data.visitsCount,
      })}`.toLowerCase(),
      comments: i18n.tp('posts.comments.count', {
        count: data.commentsConnection.totalCount,
      }),
    },
    noReactionsButton: !data.allowCommentsAndReactions,
    reactions: data.allowCommentsAndReactions
      ? {
          items: compact(
            data.newReactions.map((reaction) => {
              const emojiData = getEmojiData(reaction.code)

              let emoji = emojiData && 'native' in emojiData ? emojiData.native : undefined
              if (!emoji && /\p{Emoji}/u.test(reaction.code)) emoji = reaction.code
              if (!emoji) return

              const hasReacted = !!reaction.supporters.find(
                (supporter) => supporter.accessId === currentAccessId
              )

              return {
                emoji,
                initialCount: reaction.count,
                users: reaction.supporters.map(({ fullName }) => ({
                  name: fullName,
                })),
                onInteraction: onEmojiSelected,
                hasReacted,
              }
            })
          ),
          onInteraction: onEmojiSelected,
        }
      : undefined,
    inLabel: i18n.t('common.in'),
    comment: {
      label: i18n.t('posts.communities.write_comment'),
      onClick: () => callbacks?.onCommentClick(`${data.id}`),
    },
    onClick: () => callbacks?.onPostClick(`${data.id}`),
  }
}

const CommunityPost: FC<Props> = ({ id, onVisible, ...callbacks }) => {
  const { data } = useFragment({
    fragment: CommunityPostDataFragmentDoc,
    fragmentName: 'CommunityPostData',
    from: {
      __typename: 'PostsPost',
      id,
    },
  })

  const { ref } = useIntersectionObserver({
    threshold: 0.1,
    onChange(isIntersecting) {
      if (isIntersecting) {
        onVisible?.()
      }
    },
  })

  const normalizedData = useNormalizedCommunityPostData(
    // eslint-disable-next-line no-type-assertion/no-type-assertion
    data as CommunityPostDataFragment,
    callbacks
  )

  if (!normalizedData) return null

  return (
    // eslint-disable-next-line factorial/no-raw-html
    <div ref={ref}>
      <BaseCommunityPost {...normalizedData} />
    </div>
  )
}

export default memo(CommunityPost)
